import { ArrowLeft, Check, Crown, LockKeyhole, Mail, Sparkles } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import AppHeader from "@/components/AppHeader";

const features = [
  "Ballot history that follows you",
  "Text and complete PDF ballot exports",
  "Round Review and Flow Tracker",
  "Summary & Final Focus Review",
  "A private archive of completed rounds",
];

const tournamentPlan = {
  lookupKey: "pfjudgepro_tournament_3day",
  name: "Tournament Pass",
  price: "$4",
  detail: "one time · 72 hours of Pro",
};

const pilotPassRequestSubject = "Tournament Pilot Pass invitation request";
const pilotPassRequestBody = [
  "Hello PFJudgePro Support,",
  "",
  "I would like a Tournament Pilot Pass invitation.",
  "",
  "Please send the invitation to:",
  "Tournament name and date:",
  "",
  "Thank you!",
].join("\n");
const pilotPassMailto = [
  "mailto:support@pfjudgepro.com",
  `?subject=${encodeURIComponent(pilotPassRequestSubject)}`,
  `&body=${encodeURIComponent(pilotPassRequestBody)}`,
].join("");

export default function ProUpgrade() {
  const [isLoading, setIsLoading] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (searchParams.get("cancelled") === "1") {
      toast.info(
        "Checkout was cancelled. Your free judging tools are unchanged.",
      );
    }
  }, [searchParams]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/entitlement`, {
      credentials: "include",
    })
      .then((response) => response.json())
      .then((entitlement) => setIsPro(Boolean(entitlement.is_pro)))
      .catch(() => setIsPro(false));
  }, []);

  const startCheckout = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            lookup_key: tournamentPlan.lookupKey,
            origin_url: window.location.origin,
          }),
        },
      );
      if (!response.ok) throw new Error("Checkout request failed");
      const checkout = await response.json();
      window.location.assign(checkout.checkout_url);
    } catch (error) {
      toast.error("Checkout could not be started. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <div className="judge-app pro-page" data-testid="pro-upgrade-page">
      <AppHeader onNewRound={() => navigate("/")} isPro={isPro} />
      <main className="pro-main">
        <Link to="/" className="back-link" data-testid="return-to-ballot-link">
          <ArrowLeft size={17} />
          Return to ballot
        </Link>
        <section className="pro-hero">
          <div
            className="pro-image"
            aria-label="Notebook and fountain pen"
            data-testid="pro-upgrade-image"
          ></div>
          <div className="pro-content">
            <p className="eyebrow">
              <Crown size={14} /> PFJudgePro Pro
            </p>
            <h1>
              More clarity.
              <br />
              <em>Every round.</em>
            </h1>
            <p>
              A private Tournament Pilot Pass can unlock every Pro feature for
              one tournament. A one-time Tournament Pass is available whenever
              you are ready.
            </p>
            <section className="pilot-pass" data-testid="pilot-pass-section">
              <p className="eyebrow">
                <Sparkles size={14} /> Pilot access
              </p>
              <h2>Tournament Pilot Pass</h2>
              <p>
                Private invitations reserve 72 hours of full Pro access for one
                tournament when the judge is ready to start.
              </p>
              <a
                className="pilot-pass-button"
                href={pilotPassMailto}
                data-testid="pilot-pass-support-link"
              >
                <Mail size={18} />
                Contact support for an invitation
              </a>
            </section>
            <div className="feature-list">
              {features.map((feature) => (
                <div
                  key={feature}
                  data-testid={`pro-feature-${feature.toLowerCase().replaceAll(" ", "-")}`}
                >
                  <Check size={17} />
                  {feature}
                </div>
              ))}
            </div>
            <div className="plan-options" data-testid="pro-plan-options">
              <div
                className="plan-option active"
                data-testid="tournament-pass-plan"
              >
                <span>
                  <strong>{tournamentPlan.name}</strong>
                  <small>{tournamentPlan.detail}</small>
                </span>
                <b>{tournamentPlan.price}</b>
              </div>
            </div>
            <div
              className="plan-comparison"
              data-testid="plan-comparison-table"
            >
              <h3>Explore Pro comparison</h3>
              <div className="comparison-table-heading" data-testid="pro-comparison-heading">
                <span>Feature</span>
                <b>Free</b>
                <b>Pro</b>
              </div>
              <div>
                <span>Core judging workspace</span>
                <b>✓</b>
                <b>✓</b>
              </div>
              <div>
                <span>Round Comparison & custom criteria</span>
                <b>✓</b>
                <b>✓</b>
              </div>
              <div>
                <span>Private saved round history</span>
                <b>—</b>
                <b>✓</b>
              </div>
              <div>
                <span>Text & complete PDF ballot export</span>
                <b>—</b>
                <b>✓</b>
              </div>
              <div>
                <span>Round Review</span>
                <b>—</b>
                <b>✓</b>
              </div>
              <div>
                <span>Flow Tracker</span>
                <b>—</b>
                <b>✓</b>
              </div>
              <div>
                <span>Summary &amp; Final Focus Review</span>
                <b>—</b>
                <b>✓</b>
              </div>
            </div>
            <button
              className="pro-cta"
              onClick={startCheckout}
              disabled={isLoading}
              data-testid="start-pro-checkout-button"
            >
              <LockKeyhole size={17} />
              {isLoading
                ? "Opening secure checkout"
                : `Choose ${tournamentPlan.name} — ${tournamentPlan.price}`}
            </button>
            <small>
              <Sparkles size={14} />
              One-time Tournament Pass access is available after the pilot.
            </small>
          </div>
        </section>
      </main>
    </div>
  );
}
